import { useNavigate } from 'react-router-dom';
import { Badge, Button, Footnote, LinkButton, Modal, ProgressBar, type BadgeTone } from '../../../components/ui';
import { useCompany } from '../../../lib/companyContext';
import { formatBRLFromCents, formatCount, formatDate, formatDateTime, formatPercentFromBps, formatRatio, shortenName } from '../../../lib/format';
import { custoDoEnvio, porUnidade } from '../comparar';
import type { Broadcast, MessageTemplateSummary, Segment, SellerOption } from '../types';
import {
  BROADCAST_STATUS_LABEL,
  BROADCAST_STATUS_TONE,
  PAUSED_REASON_LABEL,
  REFUND_POLICY_TEXT,
  SEGMENT_LABEL,
  SENDER_KIND_LABEL,
  TEMPLATE_CATEGORY_LABEL,
  TEMPLATE_CATEGORY_TONE,
} from '../vocabulario';

/**
 * Relatorio de um envio: do envio ao pagamento, com custo e comissao.
 *
 * Decisoes nao obvias:
 *  1. As etapas sao aninhadas e cada uma diz o % da ANTERIOR (entregues de
 *     enviados, lidos de entregues...), com a barra sempre relativa ao total
 *     enviado -- o funil se le de olho, a taxa se le no texto.
 *  2. "Responderam" leva a tela Comercial: e la que as vendedoras atendem.
 *  3. "Pagaram" segue o ADR-001 (ultimo contato, 7 dias). Quantos a
 *     vendedora fechou aparece como "fechado por" (E5), sem tirar o credito
 *     do envio. Enquanto a janela de 7 dias esta aberta, a tela avisa que o
 *     numero ainda pode subir.
 *  4. Custo so do que saiu; "comissao por R$ 1 de custo" e a leitura de
 *     negocio do retorno (evita percentual de ROI, que ninguem le igual).
 *  5. O estorno e descrito pela politica DA EMPRESA DO ENVIO (E2): um texto
 *     fixo "reembolso nao desfaz" mentiria para quem esta em `reverte_tudo`.
 *  6. "Ver as respostas no Comercial" troca a empresa em foco para a do
 *     envio antes de navegar: a tabela mostra todas as empresas e o
 *     Comercial e da empresa em foco.
 */

const DIA_MS = 86_400_000;

interface Etapa {
  readonly chave: string;
  readonly rotulo: string;
  readonly valor: number;
  readonly base: number;
  readonly baseRotulo: string;
  readonly tom: BadgeTone;
  readonly nota?: string;
}

export function ModalRelatorioEnvio({
  envio,
  segments,
  sellers,
  templates,
  agora,
  onClose,
}: {
  readonly envio: Broadcast | null;
  readonly segments: readonly Segment[];
  readonly templates: readonly MessageTemplateSummary[];
  readonly sellers: readonly SellerOption[];
  readonly agora: Date;
  readonly onClose: () => void;
}): JSX.Element | null {
  const { companies, selectCompany } = useCompany();
  const navigate = useNavigate();
  if (envio === null) {
    return null;
  }
  const empresa = companies.find((c) => c.id === envio.companyId) ?? null;
  const politica = REFUND_POLICY_TEXT[empresa?.refundPolicy ?? 'reverte_chargeback'];
  const verComercial = (): void => {
    selectCompany(envio.companyId);
    onClose();
    navigate({ pathname: '/comercial', search: window.location.search });
  };

  const publico =
    envio.audience.source === 'csv'
      ? `lista importada${envio.audience.fileName === null ? '' : ` (${envio.audience.fileName})`}`
      : envio.audience.segments.map((k) => segments.find((s) => s.key === k)?.label ?? SEGMENT_LABEL[k]).join(', ') || 'públicos da base';
  const custo = custoDoEnvio(envio);
  const custoPorPagamento = porUnidade(custo, envio.paidCount);
  const comissaoPorReal = custo <= 0 ? null : Math.round((envio.commissionCents * 100) / custo);
  const modelo = templates.find((t) => t.id === envio.templateId);
  const temLink = modelo === undefined ? envio.clickedCount > 0 : modelo.buttons.some((b) => b.kind === 'link');
  const fimDaJanela = envio.startedAt === null ? null : new Date(new Date(envio.finishedAt ?? agora.toISOString()).getTime() + 7 * DIA_MS);
  const janelaAberta = fimDaJanela !== null && fimDaJanela.getTime() > agora.getTime();
  const atendentes = envio.assignedSellerIds.length === 0 ? 'rodízio entre as vendedoras' : sellers.filter((s) => envio.assignedSellerIds.includes(s.id)).map((s) => shortenName(s.name)).join(', ') || 'vendedoras marcadas';

  const etapas: readonly Etapa[] = [
    { chave: 'enviados', rotulo: 'Enviados', valor: envio.sentCount, base: envio.totalContacts, baseRotulo: 'do público', tom: 'brand' },
    { chave: 'entregues', rotulo: 'Chegaram no aparelho', valor: envio.deliveredCount, base: envio.sentCount, baseRotulo: 'dos enviados', tom: 'info' },
    { chave: 'lidos', rotulo: 'Leram', valor: envio.readCount, base: envio.deliveredCount, baseRotulo: 'dos entregues', tom: 'info', nota: 'só quem deixa a confirmação de leitura ligada' },
    ...(temLink ? [{ chave: 'cliques', rotulo: 'Clicaram no botão', valor: envio.clickedCount, base: envio.readCount, baseRotulo: 'dos que leram', tom: 'accent' as BadgeTone }] : []),
    { chave: 'respostas', rotulo: 'Responderam', valor: envio.repliedCount, base: envio.deliveredCount, baseRotulo: 'dos entregues', tom: 'violet', nota: `caíram com ${atendentes}` },
    { chave: 'pagaram', rotulo: 'Pagaram em até 7 dias', valor: envio.paidCount, base: envio.deliveredCount, baseRotulo: 'dos entregues', tom: 'good', nota: 'com este envio como último contato' },
  ];

  return (
    <Modal open onClose={onClose} size="lg" title={`Relatório · ${envio.name}`} description={`Criado em ${formatDateTime(envio.createdAt)} · público: ${publico}`}>
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap gap-2">
          <Badge tone={BROADCAST_STATUS_TONE[envio.status]} dot>
            {BROADCAST_STATUS_LABEL[envio.status]}
            {envio.pausedReason === null ? '' : ` · ${PAUSED_REASON_LABEL[envio.pausedReason]}`}
          </Badge>
          <Badge tone={TEMPLATE_CATEGORY_TONE[envio.templateCategory]}>{TEMPLATE_CATEGORY_LABEL[envio.templateCategory]}</Badge>
          <Badge tone="neutral">{SENDER_KIND_LABEL[envio.senderKind]}</Badge>
          <Badge tone="neutral" icon="relogio">
            {formatCount(envio.perMinute)}/min
          </Badge>
        </div>

        <section aria-labelledby="relatorio-funil" className="flex flex-col gap-3">
          <h3 id="relatorio-funil" className="text-sm font-semibold text-ink">
            Do envio ao pagamento
          </h3>
          <ol className="flex flex-col gap-2.5">
            {etapas.map((e) => (
              <li key={e.chave} className="flex flex-col gap-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                  <span className="text-ink">
                    <span className="font-semibold tabular-nums">{formatCount(e.valor)}</span> {e.rotulo.toLowerCase()}
                    {e.nota === undefined ? null : <span className="text-xs text-ink-subtle"> · {e.nota}</span>}
                  </span>
                  <span className="text-xs tabular-nums text-ink-subtle">
                    {formatRatio(e.valor, e.base)} {e.baseRotulo}
                  </span>
                </div>
                <ProgressBar value={e.valor} max={Math.max(1, envio.sentCount)} tone={e.tom} label={`${e.rotulo}: ${formatCount(e.valor)}`} />
              </li>
            ))}
          </ol>
          <p className="text-xs text-ink-subtle">
            Fora do funil: {formatCount(envio.failedCount)} falharam na entrega · {formatCount(envio.optOutCount)} pediram para sair ({formatRatio(envio.optOutCount, envio.sentCount + envio.optOutCount)})
            {envio.autoPauseOptOutBps === null ? ' · trava de opt-out desligada' : ` · trava em ${formatPercentFromBps(envio.autoPauseOptOutBps)}`}.
          </p>
        </section>

        <section aria-labelledby="relatorio-retorno" className="grid gap-3 rounded-xl border border-line bg-elevated p-4 sm:grid-cols-2">
          <h3 id="relatorio-retorno" className="sr-only">
            Retorno e custo
          </h3>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Recuperado</p>
            <p className="text-2xl font-semibold tracking-tight text-ink">{formatBRLFromCents(envio.recoveredAmountCents)}</p>
            <p className="text-xs text-ink-subtle">
              comissão de {formatBRLFromCents(envio.commissionCents)} para o Converte Fácil
              {envio.paidViaSellerCount > 0 ? ` · ${formatCount(envio.paidViaSellerCount)} ${envio.paidViaSellerCount === 1 ? 'venda fechada' : 'vendas fechadas'} pela vendedora (fechado por)` : ''}
            </p>
          </div>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
            <dt className="text-ink-subtle">Custo do envio</dt>
            <dd className="text-right tabular-nums text-ink">{custo === 0 ? 'sem tarifa' : formatBRLFromCents(custo)}</dd>
            <dt className="text-ink-subtle">Custo por recuperação</dt>
            <dd className="text-right tabular-nums text-ink">{custo === 0 ? 'sem custo' : custoPorPagamento === null ? '—' : formatBRLFromCents(custoPorPagamento)}</dd>
            <dt className="text-ink-subtle">Comissão por R$ 1 de custo</dt>
            <dd className="text-right tabular-nums text-ink">{comissaoPorReal === null ? '—' : formatBRLFromCents(comissaoPorReal)}</dd>
            <dt className="text-ink-subtle">Tarifa</dt>
            <dd className="text-right tabular-nums text-ink">{envio.costPerMessageCents === 0 ? 'número do comercial' : `${formatBRLFromCents(envio.costPerMessageCents)}/msg`}</dd>
          </dl>
        </section>

        {janelaAberta && fimDaJanela !== null ? (
          <p className="rounded-lg border border-info/40 bg-info/5 px-3 py-2 text-sm text-ink">
            A janela de atribuição fica aberta até <span className="font-medium">{formatDate(fimDaJanela.toISOString())}</span>: pagamentos deste público até lá ainda entram e são
            creditados a este envio, se ele for o último contato.
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" icon="resposta" iconRight="seta-dir" onClick={verComercial}>
            Ver as respostas no Comercial{empresa === null ? '' : ` de ${empresa.name}`}
          </Button>
          <LinkButton to="/admin/comissoes" variant="ghost" size="sm" icon="comissao">
            Extrato de comissões
          </LinkButton>
        </div>

        <Footnote>
          Contatos repetidos e números bloqueados saíram antes do envio. {politica}
        </Footnote>
      </div>
    </Modal>
  );
}
